import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Timer, Activity, ListChecks } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchReliability, fetchAutoIncident, setAutoIncident, SEV_LABEL, RE_STR as S, type ReliabilitySummary } from './reliabilityApi'

const sevColor: Record<string, string> = {
  sev1: 'text-danger', sev2: 'text-warning', sev3: 'text-secondary', sev4: 'text-tertiary',
}

function Stat({ icon, label, value, to, danger }: { icon: React.ReactNode; label: string; value: string; to?: string; danger?: boolean }) {
  const inner = (
    <div className={`rounded-lg border p-4 ${danger ? 'border-danger/40 bg-danger/5' : 'border-border-default bg-surface-2'} ${to ? 'transition-colors hover:border-accent' : ''}`}>
      <div className="flex items-center gap-1.5 text-tertiary">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className={`mt-2 text-2xl font-semibold tabular ${danger ? 'text-danger' : 'text-primary'}`}>{value}</p>
    </div>
  )
  return to ? <Link to={to}>{inner}</Link> : inner
}

export function ReliabilityDashboard() {
  const { toast } = useToast()
  const [d, setD] = useState<ReliabilitySummary | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const [autoInc, setAutoInc] = useState<boolean | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchReliability(ac.signal)
      .then(setD)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    fetchAutoIncident(ac.signal).then(setAutoInc).catch(() => setAutoInc(null))
    return () => ac.abort()
  }, [tick])

  const toggleAuto = async () => {
    const next = !autoInc
    setAutoInc(next)
    try {
      await setAutoIncident(next)
      toast(S.done, { tone: 'success' })
    } catch {
      setAutoInc(!next)
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A17.13"
        description={S.desc}
        actions={
          <button type="button" onClick={() => setTick((t) => t + 1)} className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary">
            ↻
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          {err ? (
            <ErrorState code={err.code} onRetry={() => setTick((t) => t + 1)} />
          ) : !d ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Stat icon={<AlertTriangle size={14} />} label={S.openIncidents} value={String(d.incidents.open)} to="/incidents" danger={d.incidents.open > 0} />
                <Stat icon={<Timer size={14} />} label={S.mttr} value={d.incidents.mttr_minutes > 0 ? d.incidents.mttr_minutes.toFixed(0) : '—'} to="/incidents" />
                <Stat icon={<Activity size={14} />} label={S.errorRate} value={`${d.jobs.error_rate_pct.toFixed(1)}%`} to="/job-runs" danger={d.jobs.error_rate_pct > 0} />
                <Stat icon={<ListChecks size={14} />} label={S.jobRuns} value={String(d.jobs.runs_7d)} to="/job-runs" />
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Stat icon={<AlertTriangle size={14} />} label={S.totalIncidents} value={String(d.incidents.total)} />
                <Stat icon={<AlertTriangle size={14} />} label={S.resolved} value={String(d.incidents.resolved)} />
                <Stat icon={<Activity size={14} />} label={S.jobErrors} value={String(d.jobs.errors_7d)} danger={d.jobs.errors_7d > 0} />
              </div>

              {/* Open by severity */}
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <p className="mb-3 text-xs font-medium text-secondary">{S.openBySev}</p>
                <div className="flex flex-wrap gap-6">
                  {(['sev1', 'sev2', 'sev3', 'sev4'] as const).map((sv) => (
                    <div key={sv}>
                      <p className={`text-xl font-semibold tabular ${sevColor[sv]}`}>{d.incidents.open_by_severity[sv] ?? 0}</p>
                      <p className="text-xs text-tertiary">{SEV_LABEL[sv]}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Auto-incident toggle */}
              <div className="flex items-center justify-between rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-primary">{S.autoIncident}</p>
                  <p className="text-xs text-tertiary">{S.autoIncidentHint}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoInc === true}
                  disabled={autoInc === null}
                  onClick={toggleAuto}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${autoInc ? 'bg-accent' : 'bg-surface-3'}`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${autoInc ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
